import {inject, Injectable, Type} from '@angular/core';
import {DialogService} from 'primeng/dynamicdialog';
import {DialogRef, DynamicDialogConfig} from '../model/dialog.model';

import {DialogWrapperData, DynamicDialogWrapperComponent} from '../component/dialog.component';

@Injectable({providedIn: 'root'})
export class DynamicDialogService {
  private readonly primengDialogService = inject(DialogService);

  open<TData = unknown, TResult = unknown>(
    component: Type<unknown>,
    config: DynamicDialogConfig<TData, TResult> = {}
  ): DialogRef<TResult> {
    const dialogRef = new DialogRef<TResult>();

    const wrapperData: DialogWrapperData = {
      componentType: component,
      componentData: config.data,
      dialogRef: dialogRef as DialogRef<unknown>,
      headerTemplate: config.headerTemplate,
      footerTemplate: config.footerTemplate,
    };

    const mergedStyle: Record<string, string> = {
      ...config.style,
      ...(config.minWidth ? {'min-width': config.minWidth} : {}),
      ...(config.minHeight ? {'min-height': config.minHeight} : {}),
    };

    const primengRef = this.primengDialogService.open(
      DynamicDialogWrapperComponent,
      {
        data: wrapperData,
        showHeader: !config.headerTemplate,
        header: config.headerTemplate ? undefined : config.header,
        footer: config.footer,
        ariaLabelledBy: config.ariaLabelledBy,
        closeAriaLabel: config.closeAriaLabel,

        width: config.width,
        height: config.height,
        style: Object.keys(mergedStyle).length ? mergedStyle : undefined,
        contentStyle: config.contentStyle,
        styleClass: config.styleClass,
        maskStyleClass: config.maskStyleClass,
        breakpoints: config.breakpoints,
        transitionOptions: config.transitionOptions ?? '150ms cubic-bezier(0, 0, 0.2, 1)',

        modal: config.modal ?? true,
        closable: config.closable ?? true,
        closeOnEscape: config.closeOnEscape ?? true,
        dismissableMask: config.dismissableMask ?? false,
        draggable: config.draggable ?? false,
        resizable: config.resizable ?? false,
        maximizable: config.maximizable ?? false,
        maximizeIcon: config.maximizeIcon,
        minimizeIcon: config.minimizeIcon,
        keepInViewport: config.keepInViewport ?? true,
        minX: config.minX,
        minY: config.minY,

        focusOnShow: config.focusOnShow ?? false,
        focusTrap: config.focusTrap ?? false,
        autoZIndex: config.autoZIndex ?? true,
        baseZIndex: config.baseZIndex,
        position: config.position,
        appendTo: config.appendTo ?? 'body',

        duplicate: config.duplicate ?? false,
        rtl: config.rtl ?? false,
        unstyled: config.unstyled ?? false,
      }
    );

    if (!primengRef) {
      dialogRef._resolve(undefined);
      return dialogRef;
    }

    primengRef.onClose.subscribe((result: TResult | undefined) => {
      dialogRef._resolve(result);
    });

    dialogRef._closeCallback = (result?: unknown) => {
      dialogRef._resolve(result as TResult | undefined);
      primengRef.close(result);
    };

    return dialogRef;
  }

  closeAll(): void {
    this.primengDialogService.dialogComponentRefMap.forEach((ref) => {
      ref.destroy();
    });
  }
}
